import React, { useCallback, useState } from 'react'

import styled from '@emotion/styled'

import { THUMBNAILS_URL } from 'app/settings'

import { Box, Button, CircleIconButton } from 'v2/ui'
import styledProps from 'v2/ui/utils/styledProps'

const Container = styled(Box)`
    position: relative;
    border-radius: 10%;
`

const StyledImage = styled('img')(styledProps, `object-fit: cover;`)

const isVideoOrAudio = (attachment) => {
    const type = attachment?.type
    if (type?.startsWith('audio') || type?.startsWith('video')) return true
    const filename = attachment?.filename
    return /\.(mp4|mov|ogv|webm|ogg|oga|mp3|wav)$/i.test(filename?.toLowerCase())
}

const getVideoOrAudioType = (attachment) => {
    const filename = attachment?.filename
    const extension = filename?.split('.')?.pop()?.toLowerCase()
    switch (extension) {
        case 'mp4':
        case 'mov':
            return 'video/mp4'
        case 'ogv':
        case 'ogg':
            return 'video/ogg'
        case 'webm':
            return 'video/webm'
        case 'oga':
            return 'audio/oga'
        case 'mp3':
            return 'audio/mp3'
        case 'wav':
            return 'audio/wav'
    }
    // If the files have a video type set
    const type = attachment?.type
    if (type == 'video/quicktime') return 'video/mp4'
    if (type) return type
    return 'video/mp4'
}

const isEmbed = (attachment) => {
    return (
        /\.(pdf|html|txt|htm|xml)$/i.test(attachment?.filename) ||
        /(pdf|html|xml)$/i.test(attachment?.type)
    )
}

const Attachment = ({
    canDelete,
    attachment,
    onDelete,
    size = 'sm',
    canSelect,
    onSelect,
    useFirstImageSelector,
    index,
    ...props
}) => {
    const { url, file } = useGetFile(attachment, size, index)

    const onDeleteAttachment = useCallback(
        (e) => {
            e.stopPropagation()
            e.preventDefault()
            onDelete(url)
        },
        [url, onDelete]
    )

    const onSelectAttachment = useCallback(() => {
        onSelect(url)
    }, [onSelect, url])

    const image = file
    const downloadUrl = makeUrlDownloadable(url)
    let dataAttributes = {
        'data-src': url,
        'data-download': true,
        'data-download-url': downloadUrl,
    }

    if (isVideoOrAudio(attachment)) {
        delete dataAttributes['data-src']
        dataAttributes['data-video'] = JSON.stringify({
            source: [{ src: url, type: getVideoOrAudioType(attachment) }],
            attributes: { preload: false, controls: true, fluid: true },
        })
    } else if (isEmbed(attachment)) {
        dataAttributes['data-pdf'] = true
    }

    return (
        <Box
            mb={canDelete ? 4 : 1}
            key={attachment.url}
            {...dataAttributes}
            className="attachment-gallery-item"
        >
            <Container
                width={`thumbnail.${size}`}
                height={`thumbnail.${size}`}
                mr={canDelete ? 4 : 1}
                mb={canDelete ? 4 : 1}
                flexShrink={0}
                {...props}
            >
                {image}
                {canDelete && (
                    <CircleIconButton
                        icon="x"
                        onClick={onDeleteAttachment}
                        position="absolute"
                        right="-12px"
                        top="-12px"
                    />
                )}
                {useFirstImageSelector && canSelect && (
                    <Button
                        size="sm"
                        position="absolute"
                        bottom={2}
                        left={2}
                        right={2}
                        py={1}
                        px={2}
                        onClick={onSelectAttachment}
                    >
                        Select
                    </Button>
                )}
            </Container>
        </Box>
    )
}

const makeUrlDownloadable = (url) => {
    if (url?.indexOf('filestackcontent.com') >= 0) {
        const urlObj = new URL(url)
        urlObj.searchParams.set('dl', true)
        return urlObj.toString()
    }
    return url
}

export const getAttachmentUrlAndThumbnail = (attachment, size) => {
    const thumbnailSize = size === 'sm' ? 'small' : 'large'
    let url
    let filename
    let src

    // For airtable, we return a list of json with lots of information like img thumbnails
    if (typeof attachment === 'object') {
        url = attachment.url
        filename = attachment.filename || url
        if (attachment.thumbnails) {
            src = attachment.thumbnails[thumbnailSize].url
        }
    } else {
        // If multi_file contains an array of urls, we don't display any preview image because that doesn't contain thumbnails
        url = attachment
    }

    if (!src) {
        // Display custom icon for common file extensions (supported by icons8)
        if (/\.(pdf|zip|doc|txt|html|xls|ppt)$/i.test(filename)) {
            // File type is not that useful for making icons8 icons
            const extension = filename.split('.').slice(-1).pop()
            src = `https://img.icons8.com/ios/${size}/000000/${extension}.png`
        } else if (isVideoOrAudio(attachment?.filename)) {
            src = `https://img.icons8.com/ios/${size}/000000/video-file.png`
        } else {
            src = url
        }
    }

    return { url, thumbnail: src }
}

const useGetFile = (attachment, size, index) => {
    const { url, thumbnail } = getAttachmentUrlAndThumbnail(attachment, size)
    const [src, setSrc] = useState(`${THUMBNAILS_URL}${encodeURIComponent(url)}`)
    const onError = useCallback(
        (e) => {
            // Try the specific thumbnail icon first
            if (src !== thumbnail) {
                setSrc(thumbnail)
            } else {
                e.target.onerror = null
                setSrc(`https://img.icons8.com/ios/${size}/000000/document.png`)
            }
        },
        [src, size, thumbnail]
    )

    const file = (
        <a href={url}>
            <ClickableImage
                tabIndex={0}
                data-index={index}
                border="1px solid"
                borderColor="grey.200"
                borderRadius="10%"
                size={`thumbnail.${size}`}
                p={1}
                src={src}
                alt=""
                onError={onError}
                role="button"
            />
        </a>
    )
    return { url, file }
}

const ClickableImage = styled(StyledImage)(`cursor: pointer`)

export default Attachment
